import type {
  AudioItemDetail,
  Voice,
} from '@listening/api-contract/legacy-client-types';
import {
  type UserPreferences,
  userPreferencesQueryKey,
} from '@listening/frontend/query/user';
import {
  activeDocPackOutlineNavigationItem,
  docPackOutlineNavigationItems,
} from '@listening/frontend/doc-pack/navigation';
import { docPackCatalogArgs } from '@listening/frontend/doc-pack/source';
import { menuSurface } from '@/components/web-fe/design-v2/surfaces';
import { useEq } from '@listening/frontend/query/runtime-context';
import { useDocPackUsable } from '@/lib/doc-reader/pack/doc-pack-source';
import { useIsRouteActive } from '@/hooks/use-is-route-active';
import {
  type ComponentWithClassName,
  cn,
  useContextAndErrorIfNull,
} from '@listening/utils/react-helpers';

import AddStickyNoteIcon from '@listening/assets/icons/add-sticky-note.svg?react';
import CompleteBlue from '@listening/assets/icons/icon-complete-blue.svg?react';
import VoiceSelectorIcon from '@listening/assets/icons/icon-voice-selector.svg?react';
import NextIcon from '@listening/assets/icons/next.svg?react';
import PauseIcon from '@listening/assets/icons/pause.svg?react';
import PauseCircleSmallIcon from '@listening/assets/icons/pause-circle-small.svg?react';
import PlayIcon from '@listening/assets/icons/play.svg?react';
import PlayCircleSmallIconGray from '@listening/assets/icons/play-circle-small-gray.svg?react';
import PlaybackSpeedIcon from '@listening/assets/icons/playbackSpeed.svg?react';
import PrevIcon from '@listening/assets/icons/prev.svg?react';
import SectionsIcon from '@listening/assets/icons/sections.svg?react';
import ShareIcon from '@listening/assets/icons/share2.svg?react';
import VolumeIcon from '@listening/assets/icons/volume.svg?react';

import { Button } from '@listening/frontend/components/radix/button';
import { Dialog } from '@listening/frontend/components/radix/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@listening/frontend/components/radix/dropdown-menu';
import { ScrollArea } from '@listening/frontend/components/radix/scroll-area';
import { Slider } from '@listening/frontend/components/radix/slider';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@listening/frontend/components/radix/tooltip';
import { TooltipButton } from '@listening/frontend/components/custom/tooltip-button';
import { useAudioPrefsStore } from '@listening/frontend/stores/audio-preferences-store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Backward30Seconds,
  Forward30Seconds,
} from '@/components/shared-ui/custom-icons';
import { SoundOff, WarningCircle } from 'iconoir-react';
import React, {
  type ComponentProps,
  createContext,
  use,
  useCallback,
  useState,
} from 'react';
import { useHotkey } from '@tanstack/react-hotkeys';
import * as reactUseAudioPlayer from 'react-use-audio-player';
import { toast } from 'sonner';
import { useIsClient, useMediaQuery } from 'usehooks-ts';
import {
  useAudioChunkPlayer,
  useAudioChunkSelector,
  useDocumentSummary,
  useAudioChunkPlayerSeekMethods,
  usePreferedVoice,
} from '@/lib/audio-chunk-player';
import {
  getDisplayAudioSubtitle,
  getDisplayAudioTitle,
} from '@/lib/audio-display-metadata';
import { voiceLangToLangLabel } from '@/lib/utils';
import { useVoicesList } from '@listening/frontend/query/voices';
import { useAnalytics } from '@listening/frontend/analytics/client';
import { audioService } from '@/services/audio-service';
import {
  useIsInvalidSubscription,
  useIsValidSubscription,
} from '@/services/subscription-service';
import * as m from '@/paraglide/messages';
import { CoverImgWithBackgroundNew } from '../../shared-ui/audio/cover-img-with-background';
import { Spinner } from '../../shared-ui/spinner';
import { ShareAudioDialog } from '../../web-fe/dialogs/sharing-dialogs';
import {
  PremiumDialogContext,
  usePremiumDialog,
} from '../../web-fe/misc/providers/premium-dialog-provider';
import { PlaybackSpeedDropdown as PlaybackSpeedDropdownShared } from '../read-along/playback-speed-dropdown';
import {
  useReadAlongPageType,
  useReadAlongSidebarStore,
} from '../read-along/read-along-store';
import { SeekBar } from './seek-bar';
import { rootLogger } from '@/lib/root-logger';

const moduleLogger = rootLogger.child({ module: 'play-bar' });

type SharingDialogState = 'closed' | 'copy_link' | 'embed_pdf';

type PlayBarContextType = {
  isWebAppReadalongPage: boolean;
  isVerySmallScreen: boolean;
  isSmallScreen: boolean;
  isLargeScreen: boolean;
  isMediumScreen: boolean;
  setSharingDialogState: (state: SharingDialogState) => void;
  sharingDialogState: SharingDialogState;
};
const PlayBarContext = createContext<PlayBarContextType | null>(null);

const usePlayBarContext = () => useContextAndErrorIfNull(PlayBarContext);

export function PlayBar({ className }: { className?: string }) {
  const { capture } = useAnalytics();
  const isVerySmallScreen = useMediaQuery('(max-width: 480px)');
  const isSmallScreen = useMediaQuery('(max-width: 1024px)');
  const isLargeScreen = useMediaQuery('(min-width: 1024px)');
  const isMediumScreen = useMediaQuery(
    '(min-width: 1024px) and (max-width: 1279px)',
  );

  const isWebAppReadalongPage = useIsRouteActive({
    to: '/app/readalong/$audioIdOrSlug',
    fuzzy: true,
  });

  const { audioDetailQuery } = useDocumentSummary();
  const audioItem = audioDetailQuery.data;
  const { isWebAppDoc } = useReadAlongPageType();

  const shouldShowSharing = isWebAppDoc && !!audioItem;

  const [sharingDialogState, setSharingDialogState] =
    useState<SharingDialogState>('closed');

  if (sharingDialogState !== 'closed' && !audioItem) {
    setSharingDialogState('closed');
  }

  return (
    <Dialog
      open={sharingDialogState !== 'closed' && shouldShowSharing}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          setSharingDialogState('closed');
          capture('share_document_ui_interaction', {
            interaction: 'playbar_share_modal_closed',
          });
          return;
        }
        capture('share_document_ui_interaction', {
          interaction: 'playbar_share_modal_shown',
          ...(audioItem?.id !== undefined && { audio_id: audioItem.id }),
        });
      }}
    >
      <PlayBarContext
        value={{
          isVerySmallScreen,
          isSmallScreen,
          isMediumScreen,
          isWebAppReadalongPage,
          setSharingDialogState,
          sharingDialogState,
          isLargeScreen,
        }}
      >
        <div
          className={cn(
            'v2-play-bar z-40 col-span-2 flex w-full min-w-0 shrink-0 border-t shadow-[0_-8px_30px_rgb(0_0_0/0.08)]',
            isLargeScreen ? 'h-[4.5rem] items-center gap-6 px-8' : 'flex-col',
            className,
          )}
        >
          {isLargeScreen ? (
            <>
              <AudioPreview />
              <PlaybackControls />
              {/* The wide basis makes the track absorb the row's shrink, so a
                  tight laptop width squeezes the slider before the preview's
                  title. Timestamps keep the pre-redesign inline styling. */}
              <SeekBar
                className='mx-2 basis-1/2'
                timestampClassName='text-secondary-foreground w-13 shrink-0 text-center text-xs font-[500] whitespace-nowrap'
              />
              <PlaybackOptionsButtonGroup />
            </>
          ) : (
            <>
              {/* Seek gets its own row, so it is padded like a row rather than
                  wedged between the controls — which is what left no room for
                  the timestamps and squeezed the preview. */}
              <div
                className={cn(
                  'flex w-full items-center pt-2.5 pb-1',
                  isVerySmallScreen ? 'px-2' : 'px-8',
                )}
              >
                <SeekBar trackClassName='h-1' />
              </div>
              {/* Preview and options take equal flex so the transport sits
                  centred in the bar, not just after the preview. */}
              <div
                className={cn(
                  'flex h-16 w-full min-w-0 items-center',
                  isVerySmallScreen ? 'gap-1 px-2' : 'gap-4 px-8',
                )}
              >
                <div className='flex min-w-0 flex-1 items-center'>
                  <AudioPreview />
                </div>
                <PlaybackControls />
                <div className='flex min-w-0 flex-1 items-center justify-end'>
                  <PlaybackOptionsButtonGroup />
                </div>
              </div>
            </>
          )}
        </div>
        {shouldShowSharing && (
          <>
            {sharingDialogState === 'copy_link' && (
              <ShareAudioDialog audioId={audioItem.id} />
            )}
            {/* {dialogState == 'embed_pdf' &&
            'EmbedPdfDialogContent' in sharingDialogs && (
              <sharingDialogs.EmbedPdfDialogContent audioId={audioItem.id} />
            )} */}
          </>
        )}
      </PlayBarContext>
    </Dialog>
  );
}

type AudioPreviewContainerProps = {
  className?: string;
  children?: React.ReactNode;
} & (
  | {
      songActive: true;
      audioItem: AudioItemDetail;
    }
  | { songActive: false; fallbackLinkTo?: { to: string } }
);

function PublicDocAudioPreviewContainer({
  children,
  className,
}: AudioPreviewContainerProps) {
  return <div className={className}>{children}</div>;
}

function WebAppDocAudioPreviewContainer({
  children,
  className,
  ...props
}: AudioPreviewContainerProps) {
  if (props.songActive) {
    return (
      <Link
        to='/app/readalong/$audioIdOrSlug'
        params={{
          audioIdOrSlug: props.audioItem.url_slug ?? props.audioItem.id,
        }}
        className={className}
      >
        {children}
      </Link>
    );
  }

  const fallbackTo = props.fallbackLinkTo ?? { to: '/app/home' };
  return (
    <Link {...fallbackTo} className={className}>
      {children}
    </Link>
  );
}

function AudioPreview() {
  const { isPublicDoc } = useReadAlongPageType();
  const { isLargeScreen, isSmallScreen } = usePlayBarContext();

  const AudioPreviewContainer = isPublicDoc
    ? PublicDocAudioPreviewContainer
    : WebAppDocAudioPreviewContainer;

  const { audioDetailQuery, documentSummary } = useDocumentSummary();
  const audioItem = audioDetailQuery.data;
  const lastPlayedAudios =
    audioService.secondaryQueries.useContinueListeningAudioItems();
  const fallbackAudio = lastPlayedAudios[0];
  const chapterId = useAudioChunkSelector((s) => s.chapterId);
  const docPackChunk = useAudioChunkSelector((s) => s.docPackChunk);
  const chapter = audioItem?.chapters?.find((c) => c.id === chapterId);
  const docPackUsable = useDocPackUsable(documentSummary?.doc_pack);
  const eq = useEq();
  const docPackInfo =
    documentSummary && docPackUsable ? documentSummary.doc_pack : undefined;
  const docPackCatalogQuery = useQuery({
    ...eq.queryOptions(
      docPackCatalogArgs(documentSummary?.id ?? '', docPackInfo?.url ?? ''),
    ),
    enabled: !!documentSummary && docPackInfo !== undefined,
  });
  const firstDocPackRange = docPackChunk?.ranges[0];
  const currentDocPackOutline = activeDocPackOutlineNavigationItem(
    docPackCatalogQuery.data
      ? docPackOutlineNavigationItems(docPackCatalogQuery.data.catalog)
      : [],
    firstDocPackRange
      ? { page: firstDocPackRange[0], charStart: firstDocPackRange[1] }
      : undefined,
  );

  const songActive = Boolean(audioItem?.id);
  const previewContainerProps =
    songActive && audioItem
      ? ({ songActive: true, audioItem } as const)
      : ({
          songActive: false,
          fallbackLinkTo: fallbackAudio
            ? {
                to: '/app/readalong/$audioIdOrSlug',
                params: {
                  audioIdOrSlug: fallbackAudio.url_slug ?? fallbackAudio.id,
                },
              }
            : { to: '/app/home' },
        } as const);

  const hasAudio = !!(audioItem ?? fallbackAudio);
  const previewTitle = getDisplayAudioTitle(
    audioItem?.title ?? fallbackAudio?.title,
  );
  const subtitle =
    getDisplayAudioSubtitle(
      currentDocPackOutline?.title ??
        chapter?.title ??
        fallbackAudio?.progress?.chapter_title,
    ) ?? '';

  if (!hasAudio) {
    const fallbackProps = {
      songActive: false as const,
      fallbackLinkTo: { to: '/app/home' } as const,
    };
    return (
      <AudioPreviewContainer
        className='flex flex-1 cursor-default items-center text-sm font-semibold text-[var(--v2-text-muted)]'
        {...fallbackProps}
      >
        {m.playbar_listen_first()}
      </AudioPreviewContainer>
    );
  }

  return (
    <AudioPreviewContainer
      className={cn(
        'flex min-w-0 items-center gap-3',
        // On the shared desktop row the floor keeps the title legible when the
        // row runs out of width; below it the seek bar has its own row, so the
        // preview can take more than the 45% it needed when they shared.
        isLargeScreen ? 'max-w-[260px] min-w-40' : 'max-w-[60%]',
      )}
      {...previewContainerProps}
    >
      {isLargeScreen && (
        <div className='size-12 shrink-0'>
          <CoverImgWithBackgroundNew
            coverImg={audioItem?.cover_img_url ?? fallbackAudio?.cover_img_url}
            title={previewTitle}
            size='sm'
          />
        </div>
      )}
      <PreviewText
        previewTitle={previewTitle}
        subtitle={subtitle}
        isSmallScreen={isSmallScreen}
      />
    </AudioPreviewContainer>
  );
}

function PreviewText({
  previewTitle,
  subtitle,
  isSmallScreen,
}: {
  previewTitle: string;
  subtitle: string;
  isSmallScreen: boolean;
}) {
  return (
    <div className='min-w-0 flex-1'>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <p
              className={cn(
                'truncate font-[700]',
                isSmallScreen ? 'text-xs' : 'text-sm',
              )}
            >
              {previewTitle}
            </p>
          </TooltipTrigger>
          {previewTitle.length > 35 && (
            <TooltipContent>
              <p>{previewTitle}</p>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <p
              className={cn(
                'truncate font-[500] text-secondary-foreground',
                isSmallScreen ? 'text-[11px]' : 'text-xs',
              )}
            >
              {subtitle || '\u00A0'}
            </p>
          </TooltipTrigger>
          {subtitle && (
            <TooltipContent>
              <p>{subtitle}</p>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

function PlaybackControls() {
  const {
    player: { isPlaying },
    togglePlayPause: togglePlayPauseInternal,
    isLoadingAudio,
  } = useAudioChunkPlayer();
  const audioId = useAudioChunkSelector((s) => s.audioId);
  const playbackFault = useAudioChunkSelector((s) => s.playbackFault);
  const { seekRelative } = useAudioChunkPlayerSeekMethods();
  const { isVerySmallScreen } = usePlayBarContext();
  const { isPublicDoc, isWebAppDoc } = useReadAlongPageType();
  const { capture } = useAnalytics();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const noAudioPlayPauseClicked = useCallback(() => {
    if (isPublicDoc) {
      moduleLogger.warn('noAudioPlayPauseClicked');
      return;
    }
    void audioService.utils
      .findNextAudio({ queryClient, audioId })
      .then((nextAudio) => {
        if (!nextAudio) return;
        void navigate({
          to: '/app/readalong/$audioIdOrSlug',
          params: {
            audioIdOrSlug: nextAudio.url_slug ?? nextAudio.id,
          },
        });
      });
  }, [audioId, isPublicDoc, navigate, queryClient]);

  const togglePlayPause = useCallback(() => {
    if (!audioId) {
      noAudioPlayPauseClicked();
      return;
    }
    capture('playback_controls_interaction', {
      interaction: isPlaying ? 'pause' : 'play',
    });
    togglePlayPauseInternal();
  }, [
    audioId,
    noAudioPlayPauseClicked,
    isPlaying,
    togglePlayPauseInternal,
    capture,
  ]);

  useHotkey('Space', () => {
    togglePlayPause();
  });

  const previousDocumentClicked = useCallback(async () => {
    if (isPublicDoc) return;
    const prevAudio = await audioService.utils.findPreviousAudio({
      queryClient,
      audioId,
    });
    if (!prevAudio) return;
    void navigate({
      to: '/app/readalong/$audioIdOrSlug',
      params: {
        audioIdOrSlug: prevAudio.url_slug ?? prevAudio.id,
      },
    });
  }, [audioId, isPublicDoc, navigate, queryClient]);

  const nextDocumentClicked = useCallback(async () => {
    if (isPublicDoc) return;
    const nextAudio = await audioService.utils.findNextAudio({
      queryClient,
      audioId,
    });
    if (!nextAudio) return;
    void navigate({
      to: '/app/readalong/$audioIdOrSlug',
      params: {
        audioIdOrSlug: nextAudio.url_slug ?? nextAudio.id,
      },
    });
  }, [audioId, isPublicDoc, navigate, queryClient]);

  const showSkipButtons = !isVerySmallScreen;

  return (
    <div className='flex shrink-0 items-center gap-2'>
      {showSkipButtons && isWebAppDoc && (
        <PlaybackControlButton
          Icon={PrevIcon}
          toolTipContent={m.playbar_prev_document()}
          onClick={previousDocumentClicked}
        />
      )}
      {showSkipButtons && (
        <PlaybackControlButton
          toolTipContent={m.playbar_prev_section()}
          Icon={Backward30Seconds}
          onClick={() => {
            capture('playback_controls_interaction', {
              interaction: 'skip_backward',
            });
            if (!audioId) {
              toast.info(m.playbar_no_audio());
              return;
            }
            seekRelative(-30_000);
          }}
        />
      )}

      <TooltipButton
        toolTipContent={
          playbackFault !== null
            ? m.playbar_fault_retry()
            : isPlaying
              ? m.common_pause()
              : m.common_play()
        }
        aria-label={
          playbackFault !== null
            ? m.playbar_fault_retry()
            : isPlaying
              ? m.common_pause()
              : m.common_play()
        }
        data-tour-id='play'
        className={cn(
          'size-12 shrink-0 rounded-full bg-[var(--v2-accent)] p-2 text-white shadow-sm transition-opacity hover:opacity-90 dark:bg-navy-100',
          isVerySmallScreen ? 'mx-1' : 'mx-2',
        )}
        onClick={togglePlayPause}
      >
        {playbackFault !== null ? (
          <WarningCircle className='size-4 text-white' />
        ) : isLoadingAudio ? (
          <Spinner />
        ) : isPlaying ? (
          <PauseIcon className='size-4 text-white' />
        ) : (
          <PlayIcon className='size-4 text-white' />
        )}
      </TooltipButton>
      {showSkipButtons && (
        <PlaybackControlButton
          toolTipContent={m.playbar_next_section()}
          Icon={Forward30Seconds}
          onClick={() => {
            capture('playback_controls_interaction', {
              interaction: 'skip_forward',
            });
            if (!audioId) {
              toast.info(m.playbar_no_audio());
              return;
            }
            seekRelative(30_000);
          }}
        />
      )}
      {showSkipButtons && isWebAppDoc && (
        <PlaybackControlButton
          toolTipContent={m.playbar_next_document()}
          Icon={NextIcon}
          onClick={nextDocumentClicked}
        />
      )}
    </div>
  );
}

function PlaybackControlButton({
  onClick,
  toolTipContent,
  Icon,
}: {
  Icon: ComponentWithClassName;
  toolTipContent: string;
  onClick: () => void | Promise<void>;
}) {
  const { isVerySmallScreen } = usePlayBarContext();
  const mutation = useMutation({
    onMutate: async () => {
      await onClick();
    },
  });

  return (
    <TooltipButton
      className={cn(
        'rounded-full bg-transparent text-[var(--v2-text-primary)] transition hover:bg-[var(--v2-card-bg)] hover:text-[var(--v2-text-primary)] dark:text-white dark:hover:bg-[var(--v2-card-bg)] dark:hover:text-white',
        isVerySmallScreen ? 'px-1' : 'px-2',
      )}
      variant='ghost'
      toolTipContent={toolTipContent}
      disabled={mutation.isPending}
      onClick={() => {
        mutation.mutate();
      }}
    >
      {mutation.isPending ? <Spinner /> : <Icon className='text-current' />}
    </TooltipButton>
  );
}

const useVolume = () => {
  const { setVolume } = useAudioChunkPlayer().player;
  const saveVolume = useAudioPrefsStore((state) => state.setVolume);
  const saveMuted = useAudioPrefsStore((state) => state.setMuted);
  const volume = useAudioPrefsStore((state) => state.getVolume());
  const actualVolume = useAudioPrefsStore((state) => state._volume);

  const setAndSaveVolume = (paramVolume: number) => {
    let volume = paramVolume;
    volume = Math.max(0, Math.min(1, volume));
    setVolume(volume);
    saveVolume(volume);
  };

  const setAndSaveMuted = (muted: boolean) => {
    saveMuted(muted);
    setVolume(muted ? 0 : actualVolume);
  };

  return { volume, setAndSaveVolume, setAndSaveMuted };
};

function PlaybackOptionsButtonGroup() {
  const { isSmallScreen, isLargeScreen } = usePlayBarContext();
  // Small screens collapse every option into one menu; wider ones show them
  // all inline. Nothing is ever unreachable either way.
  const inline = isSmallScreen ? [] : playBarOptionsButtons;
  const overflow = isSmallScreen ? playBarOptionsButtons : [];

  return (
    <div className='flex shrink-0 items-center gap-1 text-[var(--v2-text-muted)]'>
      {inline.map((Button) => (
        <Button key={Button.name} />
      ))}
      {overflow.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' className='shrink-0 px-2'>
              {isLargeScreen && isSmallScreen ? m.playbar_options() : '•••'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end' className={menuSurface}>
            {overflow.map((Button) => (
              <Button key={Button.name} />
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

const playBarOptionsButtons: Array<() => React.ReactNode> = [
  PlaybackSpeedButton,
  ShowSectionsButton,
  AddAudioNoteButton,
  // AddToPlaylistButton,
  ShareButton,
  VoiceSelector,
  VolumeSlider,
];

const PlaybackOptionsDropdownMenuItem = ({
  ref,
  Icon,
  label,
  subLabel,
  fragment,
  ...props
}: {
  Icon: ComponentWithClassName;
  label: string;
  subLabel?: string;
  fragment?: boolean;
} & ComponentProps<typeof DropdownMenuItem> & {
    ref?: React.RefObject<React.ComponentRef<typeof DropdownMenuItem> | null>;
  }) => {
  const content = (
    <>
      <Icon className='size-5 shrink-0 text-input' />
      <span className='text-sm font-medium'>{label}</span>
      {subLabel && (
        <span className='ml-auto text-xs text-muted-foreground'>
          {subLabel}
        </span>
      )}
    </>
  );

  if (fragment) return content;
  return (
    <DropdownMenuItem
      {...props}
      {...(ref !== undefined && { ref })}
      className='flex items-center gap-3 px-4 py-3'
    >
      {content}
    </DropdownMenuItem>
  );
};

PlaybackOptionsDropdownMenuItem.displayName = 'PlaybackOptionsDropdownMenuItem';

function VolumeSlider() {
  const { isSmallScreen, isMediumScreen } = usePlayBarContext();
  const client = useIsClient();
  const { setAndSaveVolume, setAndSaveMuted, ...hookResult } = useVolume();

  const volume = client ? hookResult.volume : 100;

  const isMuted = volume === 0;

  const handleVolumeIconClick = () => {
    setAndSaveMuted(!isMuted);
  };

  useHotkey(
    'ArrowUp',
    () => {
      setAndSaveVolume(volume + 0.1);
    },
    { preventDefault: false },
  );
  useHotkey(
    'ArrowDown',
    () => {
      setAndSaveVolume(volume - 0.1);
    },
    { preventDefault: false },
  );
  const { capture } = useAnalytics();

  const volumeSliderContent = (
    <div className='flex items-center gap-3 px-4 py-3'>
      <button
        type='button'
        onClick={handleVolumeIconClick}
        className='shrink-0 text-input transition-colors hover:text-foreground'
      >
        {isMuted ? (
          <SoundOff className='size-5' />
        ) : (
          <VolumeIcon className='size-5' />
        )}
      </button>
      <span className='text-sm font-medium'>{m.playbar_volume()}</span>
      <Slider
        rangeClassName='rounded-full bg-foreground'
        thumbClassName='invisible'
        className='ml-auto w-24'
        value={[volume]}
        max={1}
        min={0}
        step={0.01}
        onValueChange={(val) => {
          const value = val[0];
          if (value === undefined) return;
          setAndSaveVolume(value);
        }}
        onValueCommit={(val) => {
          const value = val[0];
          if (value === undefined) return;
          capture('set_playback_volume', { volume: value });
        }}
      />
      <span className='w-8 text-right text-xs text-muted-foreground'>
        {Math.round(volume * 100)}%
      </span>
    </div>
  );

  if (isSmallScreen) {
    return volumeSliderContent;
  }

  if (isMediumScreen) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <TooltipButton
            variant='ghost'
            toolTipContent={m.playbar_volume()}
            className='px-2'
          >
            {isMuted ? (
              <SoundOff className='size-6 text-input' />
            ) : (
              <VolumeIcon className='size-6 text-input' />
            )}
          </TooltipButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent className={cn('w-64', menuSurface)}>
          {volumeSliderContent}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <div className='flex items-center'>
      <TooltipButton
        variant='ghost'
        toolTipContent={isMuted ? m.playbar_unmute() : m.playbar_mute()}
        className='px-2'
        onClick={handleVolumeIconClick}
      >
        {isMuted ? (
          <SoundOff className='size-6 text-input' />
        ) : (
          <VolumeIcon className='size-6 text-input' />
        )}
      </TooltipButton>
      <Slider
        rangeClassName='rounded-full bg-foreground'
        thumbClassName='invisible'
        className='ml-2 w-28'
        value={[volume]}
        max={1}
        min={0}
        step={0.01}
        onValueChange={(val) => {
          const value = val[0];
          if (value === undefined) return;
          setAndSaveVolume(value);
        }}
        onValueCommit={(val) => {
          const value = val[0];
          if (value === undefined) return;
          capture('set_playback_volume', { volume: value });
        }}
      />
    </div>
  );
}

function PublicDocPlaybackSpeedDropdown() {
  return (
    <PlaybackSpeedDropdownShared
      isSubscriptionInvalid={false}
      setPlaybackSpeedPremiumDialog={() => {
        throw new Error('Public doc does not have premium dialog');
      }}
    />
  );
}

function WebAppPlaybackSpeedDropdown() {
  const premiumDialog = usePremiumDialog();
  return (
    <PlaybackSpeedDropdownShared
      isSubscriptionInvalid={useIsInvalidSubscription()}
      setPlaybackSpeedPremiumDialog={() => {
        premiumDialog.setCurrentPremiumDialog('playback_speed');
      }}
    />
  );
}

function PlaybackSpeedButton() {
  const playbackRate = useAudioPrefsStore((state) => state.playbackRate);
  const { isSmallScreen } = usePlayBarContext();

  const premiumDialogContext = use(PremiumDialogContext);

  const PlaybackSpeedDropdown = premiumDialogContext
    ? WebAppPlaybackSpeedDropdown
    : PublicDocPlaybackSpeedDropdown;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {isSmallScreen ? (
          <PlaybackOptionsDropdownMenuItem
            Icon={PlaybackSpeedIcon}
            label={m.playbar_speed()}
            subLabel={playbackRate === 1 ? '1.0' : playbackRate.toFixed(2)}
          />
        ) : (
          <TooltipButton
            variant='ghost'
            toolTipContent={m.playbar_speed_short()}
            className='px-2'
          >
            <PlaybackSpeedIcon className='mr-2 text-input' />
            <p className='min-w-8 text-input'>
              {playbackRate === 1 ? '1.0' : playbackRate.toFixed(2)}
            </p>
          </TooltipButton>
        )}
      </DropdownMenuTrigger>
      <PlaybackSpeedDropdown />
    </DropdownMenu>
  );
}

function ShowSectionsButton() {
  const isWebAppReadalongPage = useIsRouteActive({
    to: '/app/readalong/$audioIdOrSlug',
    fuzzy: true,
  });
  const isPublicDocPage = useIsRouteActive({
    to: '/doc/$docId',
    fuzzy: true,
  });
  const isReadalongPage = isWebAppReadalongPage || isPublicDocPage;

  const { setSidebarState, sidebarState } = useReadAlongSidebarStore();

  const { isSmallScreen } = usePlayBarContext();

  if (!isReadalongPage) return null;

  const onClick = () => {
    setSidebarState(sidebarState !== 'sections' ? 'sections' : 'closed');
  };
  const icon = <SectionsIcon className='text-input' />;
  const label = m.playbar_sections();
  if (isSmallScreen) {
    return (
      <PlaybackOptionsDropdownMenuItem
        Icon={SectionsIcon}
        label={label}
        onSelect={onClick}
      />
    );
  }
  return (
    <TooltipButton
      variant='ghost'
      toolTipContent={m.playbar_sections()}
      className='px-3'
      onClick={onClick}
    >
      {icon}
    </TooltipButton>
  );
}

function AddAudioNoteButton() {
  const isReadalongPage = useIsRouteActive({
    to: '/app/readalong/$audioIdOrSlug',
    fuzzy: true,
  });
  const { setSidebarState, sidebarState } = useReadAlongSidebarStore();

  const { isSmallScreen } = usePlayBarContext();

  const audioItem = useDocumentSummary().audioDetailQuery.data;

  const onClick = () => {
    setSidebarState(sidebarState !== 'notes' ? 'notes' : 'closed');
  };

  if (!(audioItem && isReadalongPage)) return null;

  if (isSmallScreen) {
    return (
      <PlaybackOptionsDropdownMenuItem
        Icon={AddStickyNoteIcon}
        label={m.playbar_add_highlights()}
        onSelect={onClick}
      />
    );
  }

  return (
    <TooltipButton
      variant='ghost'
      className='px-3'
      onClick={onClick}
      toolTipContent={m.playbar_add_highlights()}
    >
      <AddStickyNoteIcon className='text-input' />
    </TooltipButton>
  );
}
// function AddToPlaylistButton() {
//   return (
//     <TooltipButton
//       variant="ghost"
//       toolTipContent="Add to Playlist"
//       className="px-3"
//     >
//       <AddToPlaylistIcon className="text-input" />
//     </TooltipButton>
//   );
// }

function ShareTriggerButton(props: { onClick?: () => void }) {
  const audioItem = useDocumentSummary().audioDetailQuery.data;
  const { capture } = useAnalytics();
  if (!audioItem) return null;

  const sharing = audioItem.sharing;
  const hasUnshareableReason =
    sharing && 'unshareable_reason' in sharing && sharing.unshareable_reason;

  let tooltipText: string = m.common_share();
  if (hasUnshareableReason) {
    tooltipText =
      sharing.unshareable_reason === 'default_conversion'
        ? m.playbar_share_restriction()
        : sharing.unshareable_reason === 'copyright_risk'
          ? "This document might be copyrighted, so it can't be shared"
          : 'Cannot share this document';
  }

  return (
    <TooltipButton
      variant='ghost'
      toolTipContent={tooltipText}
      className='px-3'
      onClick={hasUnshareableReason ? undefined : props.onClick}
      onOpenChange={(isOpen) => {
        if (isOpen) {
          capture('share_document_ui_interaction', {
            interaction: hasUnshareableReason
              ? 'playbar_share_button_disabled_tooltip_shown'
              : 'playbar_share_button_tooltip_shown',
            ...(hasUnshareableReason && { unshareable_reason: tooltipText }),
          });
        }
      }}
    >
      <ShareIcon
        className={cn('text-input', hasUnshareableReason && 'text-input/60')}
      />
    </TooltipButton>
  );
}

function ShareButton() {
  const { isSmallScreen } = usePlayBarContext();

  const { isWebAppDoc } = useReadAlongPageType();
  const { setSharingDialogState } = usePlayBarContext();
  const audioItem = useDocumentSummary().audioDetailQuery.data;

  if (!isWebAppDoc) return null;
  if (!audioItem) return null;

  //   if (!('EmbedPdfDialogContent' in sharingDialogs)) {
  if (isSmallScreen) {
    return (
      <PlaybackOptionsDropdownMenuItem
        Icon={ShareIcon}
        label={m.common_share()}
        onClick={() => {
          setSharingDialogState('copy_link');
        }}
      />
    );
  }

  return (
    <ShareTriggerButton
      onClick={() => {
        setSharingDialogState('copy_link');
      }}
    />
  );
  //   }

  //   const options = (
  //     <>
  //       <DropdownMenuItem
  //         onSelect={() => {
  //           setSharingDialogState('copy_link');
  //         }}
  //       >
  //         Copy link
  //       </DropdownMenuItem>
  //       <DropdownMenuItem
  //         onClick={() => {
  //           setSharingDialogState('embed_pdf');
  //         }}
  //       >
  //         Embed PDF
  //       </DropdownMenuItem>
  //     </>
  //   );

  //   if (isSmallScreen) {
  //     return (
  //       <DropdownMenuSub>
  //         <DropdownMenuSubTrigger>
  //           <PlaybackOptionsDropdownMenuItem
  //             Icon={ShareIcon}
  //             label="Share"
  //             fragment={true}
  //           />
  //         </DropdownMenuSubTrigger>
  //         <DropdownMenuPortal>
  //           <DropdownMenuSubContent>{options}</DropdownMenuSubContent>
  //         </DropdownMenuPortal>
  //       </DropdownMenuSub>
  //     );
  //   }

  //   return (
  //     <DropdownMenu>
  //       <DropdownMenuTrigger asChild>
  //         <ShareTriggerButton />
  //       </DropdownMenuTrigger>
  //       <DropdownMenuContent className={menuSurface}>{options}</DropdownMenuContent>
  //     </DropdownMenu>
  //   );
}

function VoiceSelector() {
  const audioItem = useDocumentSummary().audioDetailQuery.data;

  const { currentVoice, prefsMutation } = usePreferedVoice();
  const isPremiumUser = useIsValidSubscription();
  const voiceOptionsQuery = useVoicesList();
  const voiceOptionsAll = voiceOptionsQuery.data ?? [];

  const voiceState = [
    currentVoice,
    {
      isPending: prefsMutation.isPending,
      mutate: (voice: string) => {
        prefsMutation.mutate({
          voice,
        });
      },
      mutateAsync: async (voice: string) => {
        await prefsMutation.mutateAsync({
          voice,
        });
      },
    },
  ] as const;

  const { isPublicDoc, isPublicTool } = useReadAlongPageType();

  const { isSmallScreen } = usePlayBarContext();
  const voicePlayer = reactUseAudioPlayer.useAudioPlayer();
  const chunkPlayer = useAudioChunkPlayer();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleDropdownOpenChange = useCallback(
    (isOpen: boolean) => {
      setIsDropdownOpen(isOpen);
      if (!isOpen) {
        setTimeout(() => {
          voicePlayer.stop();
        }, 0);
      }
    },
    [voicePlayer, setIsDropdownOpen],
  );

  const onVoiceSamplePlay = (
    e: React.MouseEvent<HTMLButtonElement>,
    voice: Voice,
  ) => {
    e.preventDefault();
    chunkPlayer.pause();
    if (voicePlayer.src !== voice.sample_url) {
      voicePlayer.load(voice.sample_url, {
        html5: true,
        autoplay: true,
        format: 'mp3',
      });
      return;
    }
    voicePlayer.togglePlayPause();
  };
  const queryClient = useQueryClient();

  if (isPublicDoc && !isPublicTool) return null;

  const [selectedVoice, updateVoiceMutation] = voiceState;

  const onVoiceSelecton = (e: Event, voiceName: string) => {
    e.preventDefault();
    if (selectedVoice.name === voiceName) return;
    chunkPlayer.pause();
    if (isPublicTool) {
      queryClient.setQueryData<UserPreferences>(
        userPreferencesQueryKey,
        (old) => ({
          most_recent_language: old?.most_recent_language,
          voice: voiceName,
        }),
      );
      chunkPlayer.reloadChunk();
      return;
    }
    void updateVoiceMutation.mutateAsync(voiceName).then(() => {
      moduleLogger.debug(
        {
          voice: voiceName,
          selectedVoice,
          cacheVoice: queryClient.getQueryData<UserPreferences>(
            userPreferencesQueryKey,
          )?.voice,
        },
        'voice selection',
      );
      chunkPlayer.reloadChunk();
    });
  };

  const availableVoiceOptions = voiceOptionsAll.filter(
    (v) => v.is_free || isPremiumUser,
  );

  const voicesMatchingLang = availableVoiceOptions.filter(
    (v) => !audioItem || v.lang === audioItem.language,
  );

  const voicesMatchingLangOrFreeIfNeeded =
    voicesMatchingLang.length === 0
      ? availableVoiceOptions.filter(
          (v) => !audioItem || v.lang === audioItem.language || v.is_free,
        )
      : voicesMatchingLang;

  const voiceOptions = voicesMatchingLangOrFreeIfNeeded;

  const voiceOptionComponents = voiceOptions.map((voice) => {
    const isActive =
      selectedVoice.name.toLowerCase() === voice.name.toLowerCase();
    return (
      <div className='relative' key={voice.name}>
        <DropdownMenuItem
          className={cn(
            'flex items-center gap-3 px-4 py-3 transition-colors duration-100',
            isActive && 'bg-[var(--v2-card-bg)]',
          )}
          onSelect={(e) => {
            onVoiceSelecton(e, voice.name);
          }}
        >
          <img
            src={voice.image_url}
            alt={voice.name}
            className='size-10 rounded-full'
          />
          <div className='flex flex-col items-start'>
            <span
              className={cn(
                'text-sm font-medium text-[var(--v2-text-primary)] transition-colors duration-100 dark:text-[var(--v2-text-muted)]',
                isActive && 'text-primary dark:text-primary',
              )}
            >
              {voice.name}
            </span>
            <span className='text-xs text-[var(--v2-text-muted)]'>
              {voice.gender === 'male'
                ? m.settings_voice_male()
                : m.settings_voice_female()}{' '}
              •{' '}
              {voice.softness === 'moderate'
                ? m.settings_voice_moderate()
                : voice.softness === 'soft'
                  ? m.settings_voice_soft()
                  : m.settings_voice_deep()}
              {voice.lang !== 'en' && ` • ${voiceLangToLangLabel(voice.lang)}`}
            </span>
          </div>
          <div className='ml-auto flex items-center gap-2'>
            {isActive && (
              <CompleteBlue className='size-6 animate-in duration-150 zoom-in-50 fade-in' />
            )}
            <div className='size-6' />
          </div>
        </DropdownMenuItem>
        <button
          type='button'
          className={cn(
            'absolute top-1/2 right-3 size-6 h-auto -translate-y-1/2 rounded-full text-muted-foreground transition-colors duration-100 hover:text-foreground',
            isActive && 'text-primary',
          )}
          onClick={(e) => {
            onVoiceSamplePlay(e, voice);
          }}
        >
          {voicePlayer.src === voice.sample_url && voicePlayer.isPlaying ? (
            <PauseCircleSmallIcon className='size-6' />
          ) : voicePlayer.src === voice.sample_url && voicePlayer.isLoading ? (
            <Spinner className='size-6' />
          ) : (
            <PlayCircleSmallIconGray className='size-6' />
          )}
        </button>
      </div>
    );
  });

  if (isSmallScreen) {
    return (
      <DropdownMenu onOpenChange={handleDropdownOpenChange}>
        <DropdownMenuTrigger asChild>
          <div className='flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-accent'>
            <VoiceSelectorIcon className='size-5 shrink-0 text-input' />
            <span className='text-sm font-medium'>{m.playbar_voice()}</span>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className={cn('max-h-[300px] w-[280px] overflow-y-auto', menuSurface)}
        >
          {voiceOptionComponents}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu onOpenChange={setIsDropdownOpen}>
      <DropdownMenuTrigger asChild>
        <TooltipButton
          variant='ghost'
          toolTipContent={m.playbar_voice()}
          className='px-2'
        >
          <VoiceSelectorIcon
            className={cn(isDropdownOpen ? 'text-primary' : 'text-input')}
          />
        </TooltipButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side='top'
        align='end'
        className='z-[100] mb-6 w-[280px] rounded-xl border border-black/5 bg-white shadow-lg sm:w-[384px] dark:border-white/10 dark:bg-[var(--v2-card-bg)]'
      >
        <div className='flex items-center gap-2 border-b px-4 py-2'>
          <VoiceSelectorIcon className='size-4 text-input' />
          <span className='text-sm font-medium'>{m.common_voices()}</span>
        </div>
        <ScrollArea className='max-h-[400px]'>
          {voiceOptionComponents}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
